import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  buildAnalyticsIngestAbsoluteUrl,
  buildAnalyticsIngestOpenApi,
  type AnalyticsIngestUnionExampleDoc,
} from './analyticsIngestOpenApi';

export interface AnalyticsIngestApiDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  siteId?: string;
  /** Report panel name — suffixes the dialog title. */
  reportName?: string;
  /** Panel-specific example body derived from Report DSL. */
  examplePayloadOverride?: Record<string, unknown>;
  /** Invertibility / derivation notes. */
  caveats?: string[];
  /** Extra UNION branch payloads. */
  unionExamples?: AnalyticsIngestUnionExampleDoc[];
  deriveConfidence?: 'high' | 'partial' | 'low';
}

const POST_GREEN = '#49cc90';
const POST_GREEN_BG = 'rgba(73, 204, 144, 0.12)';

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: '0.8125rem',
} as const;

/** Field names in schema tables — avoid secondary.main (pale gold) on light themes. */
const fieldNameSx = {
  ...monoSx,
  fontWeight: 700,
  color: (t: { palette: { mode: string; secondary: { main: string } } }) =>
    t.palette.mode === 'light' ? '#1a237e' : t.palette.secondary.main,
};

export const AnalyticsIngestApiDialog: React.FC<AnalyticsIngestApiDialogProps> = ({
  open,
  onClose,
  tenantId = '',
  siteId = '',
  reportName,
  examplePayloadOverride,
  caveats,
  unionExamples,
  deriveConfidence,
}) => {
  const [copied, setCopied] = useState(false);
  const [copiedUnionIdx, setCopiedUnionIdx] = useState<number | null>(null);

  const doc = useMemo(
    () => buildAnalyticsIngestOpenApi({
      tenantId,
      siteId,
      titleSuffix: reportName,
      examplePayloadOverride,
      caveats,
      unionExamples,
      deriveConfidence,
    }),
    [tenantId, siteId, reportName, examplePayloadOverride, caveats, unionExamples, deriveConfidence],
  );

  const absoluteUrl = useMemo(() => buildAnalyticsIngestAbsoluteUrl(doc), [doc]);
  const exampleJson = useMemo(
    () => JSON.stringify(doc.examplePayload, null, 2),
    [doc.examplePayload],
  );

  const handleCopyExample = async () => {
    try {
      await navigator.clipboard.writeText(exampleJson);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleCopyUnion = async (idx: number, payload: Record<string, unknown>) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopiedUnionIdx(idx);
      window.setTimeout(() => setCopiedUnionIdx(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
    } catch {
      /* ignore */
    }
  };

  const confidenceColor =
    doc.deriveConfidence === 'high'
      ? 'success'
      : doc.deriveConfidence === 'partial'
        ? 'warning'
        : doc.deriveConfidence === 'low'
          ? 'error'
          : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: '92vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            {doc.info.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            OpenAPI {doc.openapi} · {doc.info.version}
            {doc.deriveConfidence ? ` · derive confidence: ${doc.deriveConfidence}` : ''}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {doc.info.description}
        </Typography>

        {(doc.caveats.length > 0 || doc.deriveConfidence) && (
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 1.25,
              mb: 2.5,
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,193,7,0.08)' : 'rgba(255,193,7,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                Panel payload notes
              </Typography>
              {confidenceColor && (
                <Chip
                  size="small"
                  color={confidenceColor as 'success' | 'warning' | 'error'}
                  variant="outlined"
                  label={`confidence: ${doc.deriveConfidence}`}
                />
              )}
            </Box>
            {doc.caveats.length === 0 ? (
              <Typography variant="body2">
                Example payload was derived from invertible Report DSL filters for this panel.
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {doc.caveats.map((c) => (
                  <Typography component="li" variant="body2" key={c} sx={{ mb: 0.5 }}>
                    {c}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        )}

        {/* Servers */}
        <Typography variant="subtitle2" sx={{ mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Servers
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            px: 1.5,
            py: 1,
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
          }}
        >
          <Typography component="code" sx={{ ...monoSx, wordBreak: 'break-all' }}>
            {doc.servers[0]?.url || '/'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {doc.servers[0]?.description}
          </Typography>
        </Paper>

        {/* Operation block */}
        <Paper
          variant="outlined"
          sx={{
            borderColor: POST_GREEN,
            borderLeftWidth: 4,
            overflow: 'hidden',
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
              px: 1.5,
              py: 1.25,
              bgcolor: POST_GREEN_BG,
            }}
          >
            <Chip
              label={doc.method}
              size="small"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.5,
                bgcolor: POST_GREEN,
                color: '#fff',
                borderRadius: 1,
                minWidth: 64,
              }}
            />
            <Typography
              component="code"
              sx={{
                ...monoSx,
                fontWeight: 600,
                flex: 1,
                minWidth: 0,
                wordBreak: 'break-all',
              }}
            >
              {doc.pathTemplate}
            </Typography>
            <Tooltip title="Copy absolute URL">
              <IconButton size="small" onClick={handleCopyUrl} aria-label="Copy URL">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ px: 1.5, py: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {doc.summary}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {doc.description}
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Resolved path (from this ClickHouse configuration)
            </Typography>
            <Typography component="code" sx={{ ...monoSx, display: 'block', mb: 1.5, wordBreak: 'break-all' }}>
              {absoluteUrl}
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                px: 1.25,
                py: 1,
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(255,193,7,0.08)' : 'rgba(255,193,7,0.12)',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, displayTransform: 'uppercase' }}>
                Authorization
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {doc.securityNote}
              </Typography>
            </Paper>
          </Box>
        </Paper>

        {/* Parameters */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Parameters
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>In</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Example</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {doc.pathParams.map((p) => (
                    <TableRow key={p.name}>
                      <TableCell>
                        <Typography component="code" sx={fieldNameSx}>
                          {p.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={p.in} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography component="code" sx={monoSx}>{p.type}</Typography>
                      </TableCell>
                      <TableCell>
                        {p.required ? (
                          <Chip label="required" size="small" color="error" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">optional</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2">{p.description}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="code" sx={{ ...monoSx, wordBreak: 'break-all' }}>
                          {p.example}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Request body */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Request body
            </Typography>
            <Chip
              label="application/json"
              size="small"
              sx={{ ml: 1.5 }}
              variant="outlined"
            />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {doc.bodyOneOfNote}
            </Typography>

            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Schema — AnalyticsEvent
            </Typography>
            <TableContainer sx={{ mt: 1, mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {doc.bodyFields.map((f) => (
                    <TableRow key={f.name}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography component="code" sx={fieldNameSx}>
                          {f.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography component="code" sx={{ ...monoSx, whiteSpace: 'nowrap' }}>
                          {f.type}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {f.required ? (
                          <Chip label="required" size="small" color="error" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">optional</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{f.description}</Typography>
                        {f.example !== undefined && (
                          <Typography
                            component="code"
                            display="block"
                            sx={{ ...monoSx, mt: 0.5, color: 'text.secondary', wordBreak: 'break-all' }}
                          >
                            example: {typeof f.example === 'string' ? f.example : JSON.stringify(f.example)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Example Value
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyExample}
                color="secondary"
                variant="outlined"
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Box>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: (t) => (t.palette.mode === 'dark' ? '#1e1e1e' : '#f6f8fa'),
                overflow: 'auto',
                maxHeight: 320,
              }}
            >
              <Box
                component="pre"
                sx={{
                  ...monoSx,
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {exampleJson}
              </Box>
            </Paper>

            {doc.unionExamples.length > 1 && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Union branch examples
                </Typography>
                {doc.unionExamples.slice(1).map((branch, idx) => {
                  const realIdx = idx + 1;
                  const branchJson = JSON.stringify(branch.examplePayload, null, 2);
                  return (
                    <Box key={`${branch.label}-${realIdx}`} sx={{ mt: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 0.75,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {branch.label}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyUnion(realIdx, branch.examplePayload)}
                          color="secondary"
                          variant="outlined"
                        >
                          {copiedUnionIdx === realIdx ? 'Copied' : 'Copy'}
                        </Button>
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          bgcolor: (t) => (t.palette.mode === 'dark' ? '#1e1e1e' : '#f6f8fa'),
                          overflow: 'auto',
                          maxHeight: 240,
                        }}
                      >
                        <Box
                          component="pre"
                          sx={{
                            ...monoSx,
                            m: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {branchJson}
                        </Box>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Responses */}
        <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 0, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Responses
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {doc.responses.map((r, idx) => (
              <Box key={r.status} sx={{ mb: idx === doc.responses.length - 1 ? 0 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Chip
                    label={String(r.status)}
                    size="small"
                    color={r.status >= 200 && r.status < 300 ? 'success' : r.status >= 400 && r.status < 500 ? 'warning' : 'error'}
                    variant="outlined"
                    sx={{ fontWeight: 700, minWidth: 48 }}
                  />
                  <Typography variant="body2">{r.description}</Typography>
                </Box>
                {r.example && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      bgcolor: (t) => (t.palette.mode === 'dark' ? '#1e1e1e' : '#f6f8fa'),
                    }}
                  >
                    <Box
                      component="pre"
                      sx={{
                        ...monoSx,
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(r.example, null, 2)}
                    </Box>
                  </Paper>
                )}
                {idx < doc.responses.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnalyticsIngestApiDialog;
